/**
 * Utility functions for handling images and fallbacks
 */

/**
 * Handle image loading errors by hiding the broken image and showing fallback
 * @param {Event} e - The error event
 * @param {string} fallbackText - Text to show in fallback (usually first letter of name)
 */
export const handleImageError = (e, fallbackText = '?') => {
    const img = e.target;
    const container = img.parentElement;
    
    // Hide the broken image
    img.style.display = 'none';
    
    // Find or create fallback element
    let fallback = container.querySelector('.image-fallback');
    if (!fallback) {
        fallback = document.createElement('div');
        fallback.className = 'image-fallback w-full h-full rounded-full flex items-center justify-center text-lg sm:text-2xl font-bold bg-primary-600 text-white';
        container.appendChild(fallback);
    }
    
    // Show fallback with text
    fallback.textContent = fallbackText;
    fallback.style.display = 'flex';
};

/**
 * Get a safe image URL with fallback handling
 * @param {string} imageUrl - The image URL to check
 * @param {string} fallbackText - Text to show if image fails
 * @returns {Object} Object with url and onError handler
 */
export const getSafeImageProps = (imageUrl, fallbackText = '?') => {
    if (!imageUrl) {
        return {
            url: null,
            onError: null,
            showFallback: true
        };
    }
    
    return {
        url: imageUrl,
        onError: (e) => handleImageError(e, fallbackText),
        showFallback: false
    };
};

/**
 * Create a player avatar component with fallback
 * @param {Object} player - Player object with name and profile_picture
 * @param {string} size - Size class (e.g., 'w-10 h-10', 'w-12 h-12')
 * @param {Function} onClick - Click handler for the image
 * @returns {Object} Object with image and fallback elements
 */
export const createPlayerAvatar = (player, size = 'w-10 h-10', onClick = null) => {
    // Use profile_picture_url if available (from microservice), fallback to profile_picture
    const imageUrl = player.profile_picture_url || player.profile_picture;
    const hasImage = imageUrl;
    const fallbackText = player.name?.[0] || '?';

    const imageProps = {
        src: imageUrl,
        alt: player.name,
        className: `${size} rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity`,
        onError: (e) => handleImageError(e, fallbackText),
        onClick: onClick
    };

    const fallbackProps = {
        className: `${size} rounded-full flex items-center justify-center text-lg sm:text-2xl font-bold bg-primary-600 text-white ${hasImage ? 'hidden' : ''}`,
        children: fallbackText
    };

    return {
        image: hasImage ? imageProps : null,
        fallback: fallbackProps
    };
}; 